import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShoppingBag, ArrowRight, Clock, Layers } from "lucide-react";
import { Annotation, AnnotationBanner } from "@/components/Annotation";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#f43f5e", "#06b6d4", "#ec4899", "#84cc16"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-300 font-medium mb-1">{label || payload[0]?.name}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.payload?.fill }} className="font-mono">
          {p.name}: {p.value?.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

export default function CategoryAnalysis() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/category/analysis`).then((res) => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 skeleton-shimmer rounded" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-28 skeleton-shimmer rounded" />)}
        </div>
        <div className="h-[300px] skeleton-shimmer rounded" />
      </div>
    );
  }

  const distribution = data.distribution || [];
  const rawMultiCat = data.multi_category || {};
  const progression = data.progression || [];
  const medianDays = data.median_time_between_categories || 0;

  // Aggregate 5+ category entries
  const aggregatedDist = [];
  let fivePlusCustomers = 0;
  let fivePlusPct = 0;
  (rawMultiCat.distribution || []).forEach((d) => {
    const num = parseInt(d.categories);
    if (!isNaN(num) && num >= 5) {
      fivePlusCustomers += d.customers;
      fivePlusPct += d.pct;
    } else if (d.categories === "5+") {
      fivePlusCustomers += d.customers;
      fivePlusPct += d.pct;
    } else {
      aggregatedDist.push(d);
    }
  });
  if (fivePlusCustomers > 0) {
    aggregatedDist.push({ categories: "5+", customers: fivePlusCustomers, pct: Math.round(fivePlusPct * 10) / 10 });
  }
  const multiCat = { ...rawMultiCat, distribution: aggregatedDist };

  return (
    <div className="space-y-6" data-testid="category-analysis-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">
          Cross-Category Analysis
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Category progression tracking &middot; Repeat purchase = different category
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="kpi-card border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="cross-category-rate">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Cross-Category Rate</span>
              <Layers className="w-4 h-4 text-violet-400" />
            </div>
            <p className="font-space text-2xl font-bold tracking-tight text-violet-400">
              {multiCat.rate || 0}%
            </p>
            <p className="text-xs text-slate-500 mt-1">Customers buying 2+ categories</p>
          </CardContent>
        </Card>

        <Card className="kpi-card border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="median-days-between">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Median Days Between</span>
              <Clock className="w-4 h-4 text-amber-400" />
            </div>
            <p className="font-space text-2xl font-bold tracking-tight text-amber-400">
              {medianDays} days
            </p>
            <p className="text-xs text-slate-500 mt-1">Between category purchases</p>
          </CardContent>
        </Card>

        <Card className="kpi-card border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="total-categories">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Product Categories</span>
              <ShoppingBag className="w-4 h-4 text-emerald-400" />
            </div>
            <p className="font-space text-2xl font-bold tracking-tight text-emerald-400">
              {distribution.length}
            </p>
            <p className="text-xs text-slate-500 mt-1">Active categories tracked</p>
          </CardContent>
        </Card>
      </div>

      <Annotation>
        The {medianDays}-day median gap between category purchases maps precisely to the baby growth cycle. As children move through developmental stages (0–6 months: cribs/bedding → 6–12 months: walkers/high chairs → 1–2 years: strollers/ride-ons), parents need entirely different product categories. This creates a predictable, lifecycle-driven repurchase window. Since we sell durable gear only (no consumables), every repeat purchase is a cross-category purchase — a fundamentally different dynamic from FMCG or apparel, where same-SKU repurchase inflates repeat rates.
      </Annotation>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Category Distribution */}
        <Card className="lg:col-span-8 border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="category-distribution-detail">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-blue-400" />
              Category Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={distribution} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis
                  dataKey="category" type="category" width={200}
                  tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false} axisLine={false}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar dataKey="customers" name="Customers" radius={[0, 4, 4, 0]}>
                  {distribution.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Multi-Category Pie */}
        <Card className="lg:col-span-4 border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="multi-category-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
              <Layers className="w-4 h-4 text-violet-400" />
              Multi-Category Buyers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={multiCat.distribution || []}
                  dataKey="customers"
                  nameKey="categories"
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={80}
                  strokeWidth={1}
                  stroke="#0f172a"
                >
                  {(multiCat.distribution || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 mt-3">
              {(multiCat.distribution || []).map((d, i) => (
                <div key={d.categories} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-slate-300">{d.categories} {d.categories === "1" ? "category" : "categories"}</span>
                  </div>
                  <span className="font-mono text-slate-400">{d.pct}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <AnnotationBanner title="Category Journey Insight">
        <p>The progression table reveals the most common "first → second purchase" pathways — each row is a customer who bought from one category and organically returned for a different one. These are unprompted, lifecycle-driven expansions. Key nuance: many customers who discover us via website likely complete their second category purchase on Amazon/Flipkart (where marketplace-subsidized pricing during sales events is lower than our website). We do not compete with marketplaces on price for our own products — so the cross-category journeys captured here represent only the directly trackable subset.</p>
      </AnnotationBanner>

      {/* Category Progression */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="category-progression-table">
        <CardHeader className="pb-3">
          <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
            <ArrowRight className="w-4 h-4 text-emerald-400" />
            Category Progression Paths (First → Second Category)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">From Category</th>
                  <th className="text-center py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold"></th>
                  <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">To Category</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Customers</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Median Days</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Avg Days</th>
                </tr>
              </thead>
              <tbody>
                {progression.slice(0, 15).map((p, i) => (
                  <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                    <td className="py-2.5 px-3">
                      <Badge variant="outline" className="text-[10px] text-slate-300 border-slate-600">{p.from_category}</Badge>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <ArrowRight className="w-3.5 h-3.5 text-slate-600 mx-auto" />
                    </td>
                    <td className="py-2.5 px-3">
                      <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-800">{p.to_category}</Badge>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-300">{p.count?.toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-amber-400">{p.median_days}d</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-400">{p.avg_days}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
